"use client";

import { useState } from "react";

interface ScoreBarProps {
  label: string;
  score: number;
  max?: number;
  highlight?: boolean;
}

function ScoreBar({ label, score, max = 10, highlight = false }: ScoreBarProps) {
  const pct = Math.min(100, Math.max(0, (score / max) * 100));
  const thresholdHigh = max === 10 ? 8 : 80;
  const thresholdMid = max === 10 ? 6 : 60;
  const color =
    score >= thresholdHigh
      ? "bg-emerald-500"
      : score >= thresholdMid
        ? "bg-amber-400"
        : "bg-red-500";
  const textColor =
    score >= thresholdHigh
      ? "text-emerald-400"
      : score >= thresholdMid
        ? "text-amber-400"
        : "text-red-400";

  return (
    <div
      className={`flex items-center gap-3 rounded-lg px-2 py-1 -mx-2 ${
        highlight ? "bg-amber-950/40 ring-1 ring-amber-700/50" : ""
      }`}
    >
      <span className="w-44 shrink-0 text-sm text-slate-300 leading-tight">
        {label}
        {highlight && (
          <span className="block text-[0.65rem] font-medium text-amber-400 mt-0.5">
            prioriteta za izboljšavo
          </span>
        )}
      </span>
      <div className="flex-1 h-2 rounded-full bg-slate-700 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`w-10 text-right text-sm font-bold tabular-nums ${textColor}`}>
        {Number.isInteger(score) ? score : score.toFixed(0)}
        <span className="text-xs font-normal text-slate-600">/{max}</span>
      </span>
    </div>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-slate-700/80 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-slate-800/40 hover:bg-slate-800/70 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
        aria-expanded={open}
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          {title}
        </span>
        <span className="text-slate-500 text-sm shrink-0" aria-hidden="true">
          {open ? "−" : "+"}
        </span>
      </button>
      {open && <div className="p-4 flex flex-col gap-4 border-t border-slate-700/60">{children}</div>}
    </div>
  );
}

const VISIT_STAGE_LABELS: Record<string, string> = {
  opening: "Odprtje",
  uncovering_needs: "Odkrivanje potreb",
  solution: "Predstavitev rešitve",
  objection_handling: "Obvladovanje ugovorov",
  closing: "Zaključek",
};

const APAC_LABELS: Record<string, string> = {
  Acknowledge: "Priznanje (Acknowledge)",
  Probe: "Raziskovanje (Probe)",
  Answer: "Odgovor (Answer)",
  Confirm: "Potrditev (Confirm)",
};

/** @deprecated Legacy analyze schema — mapped for backward compatibility */
const LEGACY_SCORE_LABELS: Record<string, string> = {
  needs_discovery: "Odkrivanje potreb",
  value_proposition: "Vrednostna ponudba",
  objection_handling: "Obvladovanje ugovorov",
  closing: "Zaključek",
  communication: "Komunikacija",
};

export interface PharmacovigilanceResult {
  triggered: boolean;
  score?: number | null;
  checklist?: Record<string, boolean>;
  notes?: string;
}

export interface AnalysisCitation {
  chunk_id?: string;
  doc_name?: string;
  excerpt?: string;
  relevance?: string;
}

export interface NextDrill {
  title: string;
  focus_stage: string;
  scenario: string;
  your_goal: string;
  success_criteria: string[];
  starter_prompt: string;
}

export interface AnalysisResult {
  visit_stages?: Record<string, number>;
  /** @deprecated Use visit_stages */
  scores?: Record<string, number>;
  overall: number;
  apac?: Record<string, number> & { score?: number };
  disc_adaptation?: { score: number; notes: string[] };
  pharmacovigilance?: PharmacovigilanceResult;
  citations?: AnalysisCitation[];
  rag_meta?: {
    mode?: string;
    chunks_indexed?: number;
    chunks_retrieved?: number;
  };
  validation_meta?: {
    valid?: boolean;
    attempts?: number;
    max_attempts?: number;
    repaired?: boolean;
  };
  next_drill?: NextDrill;
  strengths: string[];
  improvements: string[];
  quotes: Array<{ text: string; note: string }>;
}

const PV_CHECKLIST_LABELS: Record<string, string> = {
  acknowledge_ae: "Priznanje in pozornost",
  collect_product: "Identifikacija zdravila",
  collect_event: "Opis dogodka",
  reporting_duty: "Obveznost poročanja",
  no_minimization: "Brez minimizacije",
};

interface SessionAnalysisProps {
  result: AnalysisResult;
  isLoading?: boolean;
  error?: string | null;
  agentId?: string;
  sessionEnded?: boolean;
  onStartNextSession?: () => void;
}

function downloadAnalysisJson(result: AnalysisResult, agentId: string) {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  const blob = new Blob([JSON.stringify(result, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `farmavoice-analiza-${agentId}-${stamp}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function resolveStageScores(result: AnalysisResult): Record<string, number> {
  if (result.visit_stages && Object.keys(result.visit_stages).length > 0) {
    return result.visit_stages;
  }
  return result.scores ?? {};
}

function findWeakestStage(
  stageScores: Record<string, number>,
): string | null {
  const entries = Object.entries(stageScores);
  if (entries.length === 0) return null;
  return entries.reduce((min, cur) => (cur[1] < min[1] ? cur : min))[0];
}

function deriveNextAction(result: AnalysisResult): string | null {
  if (result.next_drill?.title) return result.next_drill.title;
  if ((result.improvements?.length ?? 0) > 0) return result.improvements[0];
  return null;
}

export function SessionAnalysis({
  result,
  isLoading,
  error,
  agentId = "session",
  sessionEnded = false,
  onStartNextSession,
}: SessionAnalysisProps) {
  if (isLoading) {
    return (
      <div
        className="w-full max-w-3xl rounded-2xl bg-slate-900 border border-slate-800 p-6 flex flex-col items-center justify-center gap-3"
        role="status"
        aria-live="polite"
      >
        {sessionEnded && (
          <p className="text-sm font-medium text-emerald-400">Seja končana</p>
        )}
        <div className="flex items-center gap-3">
          <span className="w-4 h-4 rounded-full bg-blue-500 animate-pulse" />
          <span className="text-sm text-slate-300">
            Pripravljam mentorjevo povratno informacijo…
          </span>
        </div>
        <p className="text-xs text-slate-500">To običajno traja 15–30 sekund.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-3xl rounded-2xl bg-slate-900 border border-red-900/50 p-5">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  const stageScores = resolveStageScores(result);
  const useVisitModel = Boolean(
    result.visit_stages && Object.keys(result.visit_stages).length > 0,
  );
  const weakestStage = findWeakestStage(stageScores);
  const nextAction = deriveNextAction(result);
  const overall = result.overall ?? 0;
  const overallColor =
    overall >= 8
      ? "text-emerald-400"
      : overall >= 6
        ? "text-amber-400"
        : "text-red-400";

  const apacEntries = result.apac
    ? Object.entries(result.apac).filter(([key]) => key !== "score")
    : [];
  const apacTotal = result.apac?.score;

  const hasDetails =
    apacEntries.length > 0 ||
    apacTotal != null ||
    result.disc_adaptation ||
    result.pharmacovigilance?.triggered ||
    (result.citations?.length ?? 0) > 0 ||
    (result.quotes?.length ?? 0) > 0;

  return (
    <section
      id="session-analysis-report"
      className="w-full max-w-3xl rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden print:shadow-none print:border-slate-300"
      aria-label="Analiza prodajnega nastopa"
    >
      {/* Summary hero */}
      <div className="px-5 py-5 border-b border-slate-800 print:border-slate-300 bg-gradient-to-br from-slate-900 to-slate-800/50">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-2 min-w-0">
            <h2 className="text-base font-semibold text-slate-200 print:text-black">
              Mentorjeva povratna informacija
            </h2>
            {nextAction && (
              <p className="text-sm text-slate-300 leading-relaxed max-w-xl">
                <span className="text-slate-500">Naslednji korak: </span>
                {nextAction}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="text-xs text-slate-500 print:text-slate-600">
              Skupna ocena
            </span>
            <span
              className={`text-3xl font-bold tabular-nums ${overallColor} print:text-black`}
            >
              {overall.toFixed(1)}
              <span className="text-base font-normal text-slate-600"> / 10</span>
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4 print:hidden">
          <button
            type="button"
            onClick={() => downloadAnalysisJson(result, agentId)}
            className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Prenesi JSON
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Natisni / PDF
          </button>
          {onStartNextSession && (
            <button
              type="button"
              onClick={onStartNextSession}
              className="rounded-lg bg-blue-600 hover:bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              Nova simulacija
            </button>
          )}
        </div>
      </div>

      <div className="p-5 flex flex-col gap-5">
        {/* 5-stage scores — always visible */}
        {Object.keys(stageScores).length > 0 && (
          <div className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {useVisitModel ? "5-stopenjski model obiska" : "Ocene"}
            </h3>
            {Object.entries(stageScores).map(([key, val]) => (
              <ScoreBar
                key={key}
                label={
                  useVisitModel
                    ? (VISIT_STAGE_LABELS[key] ?? key)
                    : (LEGACY_SCORE_LABELS[key] ?? key)
                }
                score={val as number}
                highlight={useVisitModel && key === weakestStage}
              />
            ))}
          </div>
        )}

        {/* Strengths + improvements — summary level */}
        <div className="grid md:grid-cols-2 gap-4">
          {(result.strengths?.length ?? 0) > 0 && (
            <div className="rounded-xl bg-emerald-950/40 border border-emerald-900/40 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-3">
                Močne točke
              </h3>
              <ul className="flex flex-col gap-2">
                {result.strengths.slice(0, 3).map((s, i) => (
                  <li
                    key={i}
                    className="flex gap-2 text-sm text-slate-300 leading-snug"
                  >
                    <span className="mt-0.5 text-emerald-500 shrink-0">✓</span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(result.improvements?.length ?? 0) > 0 && (
            <div className="rounded-xl bg-amber-950/30 border border-amber-900/30 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-400 mb-3">
                Priložnosti za izboljšavo
              </h3>
              <ul className="flex flex-col gap-2">
                {result.improvements.slice(0, 3).map((s, i) => (
                  <li
                    key={i}
                    className="flex gap-2 text-sm text-slate-300 leading-snug"
                  >
                    <span className="mt-0.5 text-amber-400 shrink-0">→</span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Next drill — prominent CTA */}
        {result.next_drill && (
          <div className="rounded-xl bg-blue-950/30 border border-blue-900/40 p-4 print:border-slate-300">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-400">
                Naslednja vaja (~2 min)
              </h3>
              <span className="text-[0.65rem] text-slate-500">
                Fokus:{" "}
                {VISIT_STAGE_LABELS[result.next_drill.focus_stage] ??
                  result.next_drill.focus_stage}
              </span>
            </div>
            <p className="text-sm font-medium text-slate-100 mb-2">
              {result.next_drill.title}
            </p>
            <p className="text-sm text-slate-300 leading-snug mb-3">
              {result.next_drill.your_goal}
            </p>
            <blockquote className="border-l-2 border-blue-600 pl-3 text-sm text-slate-200 italic leading-relaxed mb-4">
              &ldquo;{result.next_drill.starter_prompt}&rdquo;
            </blockquote>
            {onStartNextSession && (
              <button
                type="button"
                onClick={onStartNextSession}
                className="w-full sm:w-auto rounded-xl bg-blue-600 hover:bg-blue-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 print:hidden"
              >
                Začni naslednjo simulacijo
              </button>
            )}
          </div>
        )}

        {/* Collapsible details */}
        {hasDetails && (
          <CollapsibleSection title="Podrobna analiza" defaultOpen={false}>
            {(apacEntries.length > 0 || apacTotal != null) && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    APAC — obravnava ugovorov
                  </h4>
                  {apacTotal != null && (
                    <span className="text-sm font-bold text-blue-400 tabular-nums">
                      {apacTotal}
                      <span className="text-xs font-normal text-slate-600">
                        {" "}
                        / 100
                      </span>
                    </span>
                  )}
                </div>
                {apacEntries.map(([key, val]) => (
                  <ScoreBar
                    key={key}
                    label={APAC_LABELS[key] ?? key}
                    score={val as number}
                    max={100}
                  />
                ))}
              </div>
            )}

            {result.disc_adaptation && (
              <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    DISC prilagoditev
                  </h4>
                  <span className="text-sm font-bold text-violet-400 tabular-nums">
                    {result.disc_adaptation.score}
                    <span className="text-xs font-normal text-slate-600">
                      {" "}
                      / 100
                    </span>
                  </span>
                </div>
                {(result.disc_adaptation.notes?.length ?? 0) > 0 && (
                  <ul className="flex flex-col gap-2">
                    {result.disc_adaptation.notes.map((note, i) => (
                      <li
                        key={i}
                        className="text-sm text-slate-300 leading-snug flex gap-2"
                      >
                        <span className="text-violet-400 shrink-0">•</span>
                        {note}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {result.pharmacovigilance?.triggered && (
              <div className="rounded-xl bg-rose-950/30 border border-rose-900/40 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-rose-400">
                    Farmakovigilanca (PV)
                  </h4>
                  {result.pharmacovigilance.score != null && (
                    <span className="text-sm font-bold text-rose-300 tabular-nums">
                      {result.pharmacovigilance.score}
                      <span className="text-xs font-normal text-slate-600">
                        {" "}
                        / 100
                      </span>
                    </span>
                  )}
                </div>
                {result.pharmacovigilance.notes && (
                  <p className="text-sm text-slate-300 mb-3 leading-snug">
                    {result.pharmacovigilance.notes}
                  </p>
                )}
                {result.pharmacovigilance.checklist &&
                  Object.keys(result.pharmacovigilance.checklist).length > 0 && (
                    <ul className="flex flex-col gap-2">
                      {Object.entries(result.pharmacovigilance.checklist).map(
                        ([key, passed]) => (
                          <li
                            key={key}
                            className="flex gap-2 text-sm text-slate-300 leading-snug"
                          >
                            <span
                              className={
                                passed ? "text-emerald-400" : "text-rose-400"
                              }
                            >
                              {passed ? "✓" : "✗"}
                            </span>
                            {PV_CHECKLIST_LABELS[key] ?? key}
                          </li>
                        ),
                      )}
                    </ul>
                  )}
              </div>
            )}

            {(result.quotes?.length ?? 0) > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                  Ključni momenti
                </h4>
                <div className="flex flex-col gap-3">
                  {result.quotes.map((q, i) => (
                    <blockquote
                      key={i}
                      className="border-l-2 border-blue-700 pl-4 flex flex-col gap-1"
                    >
                      <p className="text-sm text-slate-200 italic leading-relaxed">
                        &ldquo;{q.text}&rdquo;
                      </p>
                      <p className="text-xs text-slate-500 leading-snug">
                        {q.note}
                      </p>
                    </blockquote>
                  ))}
                </div>
              </div>
            )}

            {(result.citations?.length ?? 0) > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                  Viri (SmPC)
                </h4>
                <div className="flex flex-col gap-3">
                  {(result.citations ?? []).map((c, i) => (
                    <div
                      key={c.chunk_id ?? i}
                      className="rounded-lg border border-slate-700 bg-slate-800/40 p-3"
                    >
                      <p className="text-xs font-medium text-blue-400 mb-1">
                        {c.doc_name}
                      </p>
                      {c.excerpt && (
                        <p className="text-sm text-slate-300 leading-snug mb-1">
                          {c.excerpt}
                        </p>
                      )}
                      {c.relevance && (
                        <p className="text-xs text-slate-500">{c.relevance}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Full strengths/improvements if more than 3 */}
            {(result.strengths?.length ?? 0) > 3 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Vse močne točke
                </h4>
                <ul className="flex flex-col gap-1.5">
                  {result.strengths.slice(3).map((s, i) => (
                    <li key={i} className="text-sm text-slate-300">
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(result.improvements?.length ?? 0) > 3 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Vse priložnosti
                </h4>
                <ul className="flex flex-col gap-1.5">
                  {result.improvements.slice(3).map((s, i) => (
                    <li key={i} className="text-sm text-slate-300">
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.next_drill &&
              (result.next_drill.scenario ||
                (result.next_drill.success_criteria?.length ?? 0) > 0) && (
                <div className="text-sm text-slate-400">
                  <p className="mb-2">{result.next_drill.scenario}</p>
                  {(result.next_drill.success_criteria?.length ?? 0) > 0 && (
                    <ul className="flex flex-col gap-1">
                      {result.next_drill.success_criteria.map((c, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-blue-400 shrink-0">□</span>
                          {c}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
          </CollapsibleSection>
        )}
      </div>
    </section>
  );
}
